import React, { useEffect } from 'react';
import { useColorMode } from '../contexts/ColorModeContext';
import { useAuth } from '../hooks/use-auth';

const Debug: React.FC = () => {
  const { colorMode, toggleColorMode } = useColorMode();
  const { user, session, isLoading } = useAuth();
  const isAuthenticated = !!session;

  // Log rendering information
  useEffect(() => {
    console.log('Debug component mounted with:', {
      colorMode,
      isAuthenticated,
      isLoading,
      user
    });
  }, [colorMode, isAuthenticated, isLoading, user]);

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-3xl font-bold mb-6 text-[var(--text-primary)]">
        KaiMed Debug Page
      </h1>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-[var(--background-inset)] p-6 rounded-lg shadow">
          <h2 className="text-xl font-semibold mb-4 text-[var(--text-primary)]">Theme Information</h2>
          <p className="text-[var(--text-secondary)] mb-2">
            Current mode: <span className="font-medium text-[var(--text-primary)]">{colorMode}</span>
          </p>
          <button 
            onClick={toggleColorMode}
            className="mt-2 px-4 py-2 bg-[var(--brand-primary)] text-white rounded hover:bg-[var(--brand-primary-hover)] transition-colors"
          >
            Toggle Theme
          </button>
        </div>
        
        <div className="bg-[var(--background-inset)] p-6 rounded-lg shadow">
          <h2 className="text-xl font-semibold mb-4 text-[var(--text-primary)]">Authentication Status</h2>
          <p className="text-[var(--text-secondary)] mb-1">
            Status: <span className="font-medium text-[var(--text-primary)]">
              {isLoading ? 'Loading...' : isAuthenticated ? 'Authenticated' : 'Not Authenticated'}
            </span>
          </p>
          {isAuthenticated && user && (
            <div className="mt-3">
              <p className="text-[var(--text-secondary)] mb-1">
                User: <span className="font-medium text-[var(--text-primary)]">{user.firstName} {user.lastName}</span>
              </p>
              <p className="text-[var(--text-secondary)] mb-1">
                Email: <span className="font-medium text-[var(--text-primary)]">{user.email}</span>
              </p>
              <p className="text-[var(--text-secondary)]">
                Role: <span className="font-medium text-[var(--text-primary)]">{user.rank ?? 'patient'}</span>
              </p>
            </div>
          )}
          <div className="mt-4">
            <button 
              onClick={() => {
                localStorage.setItem('debug_bypass_auth', 'true');
                window.location.reload();
              }}
              className="px-4 py-2 mr-3 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors"
            >
              Enable Auth Bypass
            </button>
            <button 
              onClick={() => {
                localStorage.removeItem('debug_bypass_auth');
                window.location.reload();
              }}
              className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
            >
              Disable Auth Bypass
            </button>
          </div>
        </div>
      </div>
      
      <div className="bg-[var(--background-inset)] p-6 rounded-lg shadow mb-8">
        <h2 className="text-xl font-semibold mb-4 text-[var(--text-primary)]">Visual Tests</h2>
        <div className="space-y-4">
          <div className="p-4 bg-[var(--background-surface)] rounded-md">
            <p className="text-[var(--text-primary)]">Primary Text</p>
            <p className="text-[var(--text-secondary)]">Secondary Text</p>
            <p className="text-[var(--text-tertiary)]">Tertiary Text</p>
          </div>
          
          <div className="p-4 bg-[var(--background-interactive)] rounded-md">
            <p className="text-[var(--status-success)]">Success Text</p>
            <p className="text-[var(--status-warning)]">Warning Text</p>
            <p className="text-[var(--status-error)]">Error Text</p>
            <p className="text-[var(--status-info)]">Info Text</p>
          </div>
          
          <div className="p-4 rounded-md flex space-x-2">
            <button className="px-4 py-2 bg-[var(--brand-primary)] text-white rounded">
              Primary Button
            </button>
            <button className="px-4 py-2 bg-[var(--background-interactive)] text-[var(--text-primary)] rounded">
              Secondary Button
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Debug; 